import React, { useRef, useState } from 'react';
import {
  Button, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, LinearProgress, Alert,
} from '@mui/material';
import { Upload } from '@mui/icons-material';
import { createTender } from '../../services/api';
import { useTenders } from '../../hooks/TenderContext';
import { parseCSV } from '../../utils/csv';
import toast from 'react-hot-toast';

// Map Hebrew CSV headers -> tender field names. Unmatched columns are ignored.
const HEADER_MAP = {
  'מספר מכרז': 'tender_number',
  'מזמין': 'client',
  'נושא המכרז': 'subject',
  'תחום': 'field',
  'סטטוס': 'status',
  'מנהל': 'manager',
  'גורם מטפל': 'handler',
  'מועד הגשה': 'submission_date',
  'תעדוף': 'priority',
  'אופן זכייה': 'win_method',
  'תוצאה': 'win_status',
  'הערות': 'notes',
};

const ImportCsvTenders = () => {
  const { loadTenders } = useTenders();
  const fileInputRef = useRef(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [summary, setSummary] = useState(null); // { success, failed }

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    event.target.value = ''; // allow re-selecting the same file later
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const { headers, rows } = parseCSV(e.target.result);
        if (!headers.length || !rows.length) {
          toast.error('קובץ CSV ריק או לא תקין');
          return;
        }

        // Build the tender objects from mapped columns
        const tenders = rows
          .map((row) => {
            const tender = {};
            headers.forEach((header, ci) => {
              const field = HEADER_MAP[String(header).trim()];
              if (field) {
                tender[field] = row[ci] != null ? String(row[ci]).trim() : '';
              }
            });
            return tender;
          })
          // Skip completely empty rows / rows without the required client+subject
          .filter((t) => (t.client && t.client.trim()) || (t.subject && t.subject.trim()));

        if (!tenders.length) {
          toast.error('לא נמצאו שורות תקינות לייבוא');
          return;
        }

        await runImport(tenders);
      } catch (err) {
        console.error(err);
        toast.error('שגיאה בקריאת הקובץ');
      }
    };
    reader.onerror = () => toast.error('שגיאה בקריאת הקובץ');
    reader.readAsText(file, 'UTF-8');
  };

  const runImport = async (tenders) => {
    setSummary(null);
    setImporting(true);
    setProgress({ done: 0, total: tenders.length });
    setDialogOpen(true);

    let success = 0;
    let failed = 0;

    for (let idx = 0; idx < tenders.length; idx += 1) {
      try {
        await createTender({ ...tenders[idx], category: 'active' });
        success += 1;
      } catch (err) {
        failed += 1;
      }
      setProgress({ done: idx + 1, total: tenders.length });
    }

    setImporting(false);
    setSummary({ success, failed });

    // Refresh the tender list from the server
    try {
      await loadTenders();
    } catch (err) {
      /* loadTenders already surfaces its own error toast */
    }

    toast.success(`${success} מכרזים יובאו בהצלחה${failed > 0 ? `, ${failed} נכשלו` : ''}`);
  };

  const handleClose = () => {
    if (importing) return; // don't allow closing mid-import
    setDialogOpen(false);
    setSummary(null);
    setProgress({ done: 0, total: 0 });
  };

  const percent = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <>
      <Tooltip title="ייבוא מכרזים מקובץ CSV">
        <Button
          variant="outlined"
          size="small"
          startIcon={<Upload />}
          onClick={handleButtonClick}
          color="primary"
        >
          ייבוא CSV
        </Button>
      </Tooltip>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        hidden
        onChange={handleFileSelect}
      />

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="xs" fullWidth dir="rtl">
        <DialogTitle>ייבוא מכרזים מ-CSV</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 1 }}>
            {importing ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  מייבא {progress.done} מתוך {progress.total}...
                </Typography>
                <LinearProgress variant="determinate" value={percent} />
              </>
            ) : summary ? (
              <Alert severity={summary.failed > 0 ? 'warning' : 'success'}>
                {summary.success} מכרזים יובאו בהצלחה
                {summary.failed > 0 ? `, ${summary.failed} נכשלו` : ''}
              </Alert>
            ) : (
              <Typography variant="body2" color="text.secondary">
                מעבד את הקובץ...
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={importing} variant="contained">
            סגור
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ImportCsvTenders;
